import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dropdown"]

  connect() {
    this.selectedIndex = -1
    const suggestionsEl = this.element.closest("[data-addon-suggestions]")
    this.suggestions = suggestionsEl ? JSON.parse(suggestionsEl.dataset.addonSuggestions) : []
    this._handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("click", this._handleClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this._handleClickOutside)
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }

  get nameInput() {
    return this.element.querySelector("input[type='text']")
  }

  get entry() {
    return this.element.closest("[data-service-addons-target='entry']")
  }

  search() {
    const query = this.nameInput.value.trim().toLowerCase()

    if (query.length < 2) {
      this.hideDropdown()
      return
    }

    const matches = this.suggestions.filter(s =>
      s.name.toLowerCase().includes(query)
    )

    this.renderDropdown(matches, query)
  }

  // "150", "49,99" – the number part of Pln.format.
  amount(value) {
    const number = Number(value)
    const decimals = Number.isInteger(number) ? 0 : 2
    return number.toLocaleString("pl-PL", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
  }

  renderDropdown(matches, query) {
    if (matches.length === 0) {
      this.hideDropdown()
      return
    }

    let html = ""
    matches.forEach((addon, index) => {
      const highlightedName = this.highlightMatch(addon.name, query)
      const priceText = addon.price_max
        ? `${this.amount(addon.price)} – ${this.amount(addon.price_max)} zł`
        : `${this.amount(addon.price)} zł`
      html += `<div class="px-3 py-2 cursor-pointer hover:bg-base-200 ${index === this.selectedIndex ? 'bg-base-200' : ''} text-sm border-b border-base-200 last:border-0"
                   data-action="click->addon-autofill#select"
                   data-addon-id="${addon.id || ''}"
                   data-name="${this.escapeAttr(addon.name)}"
                   data-price="${addon.price}"
                   data-price-max="${addon.price_max || ''}"
                   data-duration="${addon.duration}">
                <div class="font-medium">${highlightedName}</div>
                <div class="text-xs opacity-60">${priceText} · ${addon.duration} min</div>
              </div>`
    })

    this.dropdownTarget.innerHTML = html
    this.dropdownTarget.classList.remove("hidden")
  }

  highlightMatch(text, query) {
    const regex = new RegExp(`(${this.escapeRegex(query)})`, "gi")
    return this.escapeHtml(text).replace(regex, '<strong class="text-primary">$1</strong>')
  }

  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  }

  escapeHtml(string) {
    const div = document.createElement("div")
    div.textContent = string
    return div.innerHTML
  }

  escapeAttr(string) {
    return String(string)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  }

  select(event) {
    const item = event.currentTarget
    const entry = this.entry

    this.nameInput.value = item.dataset.name

    entry.querySelectorAll("input").forEach(input => {
      if (input.name.endsWith("[price]")) {
        input.value = item.dataset.price
      } else if (input.name.endsWith("[price_max]")) {
        input.value = item.dataset.priceMax || ""
      } else if (input.name.endsWith("[duration]")) {
        input.value = item.dataset.duration
      }
    })

    const addonIdInput = entry.querySelector("input[name$='[addon_id]']")
    if (addonIdInput) {
      addonIdInput.value = item.dataset.addonId || ""
    }

    this.hideDropdown()
  }

  hideDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add("hidden")
    }
    this.selectedIndex = -1
  }

  handleKeydown(event) {
    if (!this.hasDropdownTarget || this.dropdownTarget.classList.contains("hidden")) {
      return
    }

    const items = this.dropdownTarget.querySelectorAll("[data-action]")

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1)
        this.updateSelection(items)
        break
      case "ArrowUp":
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.updateSelection(items)
        break
      case "Enter":
        event.preventDefault()
        if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
          items[this.selectedIndex].click()
        }
        break
      case "Escape":
        this.hideDropdown()
        break
    }
  }

  updateSelection(items) {
    items.forEach((item, index) => {
      item.classList.toggle("bg-base-200", index === this.selectedIndex)
    })
  }
}
